// Util de CNPJ alfanumérico (Receita Federal/SERPRO, a partir de 01/07/2026).
// As 12 primeiras posições passam a aceitar letras A-Z; os 2 dígitos verificadores
// finais continuam numéricos. CNPJs 100% numéricos antigos seguem válidos pelo mesmo
// cálculo. Formato mascarado: XX.XXX.XXX/XXXX-XX.
//
// Para CPF e CNPJ numérico, cleanCnpj() produz o mesmo que replace(/\D/g,''),
// então a troca é retrocompatível. NUNCA use replace(/\D/g) em CNPJ (apaga letras).

export const cleanCnpj = (value?: string | number | null): string =>
  String(value ?? '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');

// Máscara alfanumérica: 12 posições A-Z/0-9 + 2 DV numéricos.
export const maskCnpj = (value?: string | number | null): string => {
  const clean = cleanCnpj(value);
  const raiz = clean.slice(0, 12);
  const dv = clean.slice(12, 14).replace(/[^0-9]/g, '');
  const chars = raiz + dv;
  let masked = '';
  for (let i = 0; i < chars.length; i += 1) {
    if (i === 2 || i === 5) masked += '.';
    if (i === 8) masked += '/';
    if (i === 12) masked += '-';
    masked += chars[i];
  }
  return masked;
};

// Validação do DV pelo algoritmo oficial SERPRO: valor de cada caractere = código
// ASCII - 48 (0-9 -> 0-9, A-Z -> 17-42); pesos cíclicos de 2 a 9 da direita para a
// esquerda; módulo 11. Cobre alfanumérico e numérico, com ou sem máscara.
export const validateCnpj = (value?: string | number | null): boolean => {
  const clean = cleanCnpj(value);
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(clean)) return false;
  if (/^(.)\1{13}$/.test(clean)) return false;

  const calcDv = (base: string): number => {
    let sum = 0;
    let weight = 2;
    for (let i = base.length - 1; i >= 0; i -= 1) {
      sum += (base.charCodeAt(i) - 48) * weight;
      weight = weight === 9 ? 2 : weight + 1;
    }
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };

  const base = clean.slice(0, 12);
  const dv1 = calcDv(base);
  const dv2 = calcDv(base + dv1);
  return clean.slice(12) === `${dv1}${dv2}`;
};

// Heurística "é um CNPJ?" (vs. razão social / nome livre): true quando o valor, limpo,
// é exatamente a estrutura de um CNPJ (12 posições alfanuméricas + 2 dígitos). Um nome de
// empresa não casa essa estrutura. Substitui o antigo /^\d/ (que quebrava p/ CNPJ começando
// com letra).
export const looksLikeCnpj = (value?: string | number | null): boolean =>
  /^[0-9A-Z]{12}[0-9]{2}$/.test(cleanCnpj(value));

// Máscara combinada CPF/CNPJ: CPF (<= 11 dígitos, sem letras) continua numérico;
// caso contrário, trata como CNPJ alfanumérico.
export const maskCpfCnpj = (value?: string | number | null): string => {
  const clean = cleanCnpj(value);
  const digits = clean.replace(/[^0-9]/g, '');
  if (clean.length <= 11 && clean === digits) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return maskCnpj(clean);
};

export const shouldShowCnpjError = (value?: string | null, touched = false): boolean => {
  const clean = cleanCnpj(value);
  if (clean === '') return false;
  if (validateCnpj(clean)) return false;
  return touched || clean.length >= 14;
};
